// apps/frontend/src/components/missions/ShoppingMissionWizard.tsx
// Wizard 3 etapes pour capturer l'intention multi-articles d'un acheteur.
//
// Etape 0 — Pour qui ? (profil famille ou moi) avec pre-fill auto des
//          tailles/couleurs/styles depuis le profil selectionne.
// Etape 1 — Items : la vendeuse ajoute une ou plusieurs cards "item"
//          chacune avec category, sub-style optionnel, couleurs, qty,
//          taille override. Permet "1 jean slim bleu + 1 jean baggy noir
//          + 2 t-shirts blancs" comme demande utilisatrice.
// Etape 2 — Budget global + notes optionnelles.
//
// Pre-fill profile : on lit profile.size_preferences.clothes/shoes pour
// preremplir la taille de chaque item, et color_preferences/style_preferences
// pour suggerer en chips actifs par defaut.

import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Sparkles, ArrowRight, Check, Plus, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../../config/api';
import { analytics } from '../../lib/analytics';
import { getMissionCategories, MISSION_COLORS } from './missionCatalog';
import type { ProductProfileType } from '@mode-app/shared';

interface FamilyProfile {
  id: string;
  profile_name: string;
  profile_type: string;
  age: number | null;
  gender?: string | null;
  avatar_url: string | null;
  size_preferences?: { clothes?: string; shoes?: string };
  color_preferences?: Record<string, unknown> | string[];
  style_preferences?: Record<string, unknown> | string[];
  brand_preferences?: Record<string, unknown> | string[];
}

interface MissionItem {
  id: string; // local uuid pour la liste
  category: string; // key de groupe (pulls, pantalons, robes...)
  subStyle?: string; // item precis (jean-slim, robe-courte...)
  colors: string[]; // keys de MISSION_COLORS
  quantity: number;
  sizeOverride?: string; // ecrase la taille profile si rempli
}

// Map profile_type + gender + age vers ProductProfileType de la taxonomie
// shared. Si rien ne match, fallback ADULT_WOMAN (catalogue le plus riche).
function profileToProductProfile(
  profile: FamilyProfile | null
): ProductProfileType {
  if (!profile) return 'ADULT_WOMAN';
  const isMale =
    (profile.gender || '').toUpperCase().startsWith('M') ||
    (profile.gender || '').toUpperCase() === 'BOY';
  const age = profile.age ?? 99;
  if (profile.profile_type === 'CHILD' || age < 18) {
    if (age <= 2) return isMale ? 'BABY_BOY' : 'BABY_GIRL';
    return isMale ? 'CHILD_BOY' : 'CHILD_GIRL';
  }
  return isMale ? 'ADULT_MAN' : 'ADULT_WOMAN';
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  defaultProfileId?: string;
}

const BUDGET_PRESETS = [
  { value: 30, label: '~30€' },
  { value: 50, label: '~50€' },
  { value: 80, label: '~80€' },
  { value: 120, label: '~120€' },
  { value: 200, label: '~200€' },
  { value: null, label: 'Pas de limite' },
];

const newItemId = () =>
  `item_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;

// Extrait un array de strings d'un champ JSON pref (peut etre
// un array, un objet { key: true }, ou une string CSV).
const prefsToArray = (
  pref: Record<string, unknown> | string[] | undefined | null
): string[] => {
  if (!pref) return [];
  if (Array.isArray(pref)) return pref.map(String).filter(Boolean);
  if (typeof pref === 'object') {
    return Object.entries(pref)
      .filter(([, v]) => v === true || (typeof v === 'number' && v > 0))
      .map(([k]) => k);
  }
  return [];
};

const ShoppingMissionWizard: React.FC<Props> = ({
  isOpen,
  onClose,
  defaultProfileId,
}) => {
  const navigate = useNavigate();
  const [step, setStep] = useState<0 | 1 | 2>(0);
  const [selectedProfile, setSelectedProfile] = useState<string | null>(
    defaultProfileId ?? null
  );
  const [items, setItems] = useState<MissionItem[]>([
    { id: newItemId(), category: 'pulls', colors: [], quantity: 1 },
  ]);
  const [budget, setBudget] = useState<number | null>(80);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [profiles, setProfiles] = useState<FamilyProfile[]>([]);
  const [loadingProfiles, setLoadingProfiles] = useState(false);
  const [expandedItemId, setExpandedItemId] = useState<string | null>(null);

  // Reset propre quand on ouvre/ferme + track abandons
  const lastReachedStep = useRef(0);
  const submittedRef = useRef(false);
  useEffect(() => {
    if (!isOpen) {
      // Abandon detecte : modal ferme sans submit (l'event mission_created
      // n'a pas tire). Capture le step atteint et le nb d'items pour
      // identifier ou la friction se trouve.
      if (lastReachedStep.current > 0 && !submittedRef.current) {
        analytics.missionWizardAbandoned(lastReachedStep.current, items.length);
      }
      setStep(0);
      setError(null);
      lastReachedStep.current = 0;
      submittedRef.current = false;
    }
  }, [isOpen, items.length]);

  // Track changement de step (entre dans le wizard)
  useEffect(() => {
    if (!isOpen) return;
    lastReachedStep.current = Math.max(lastReachedStep.current, step);
    analytics.missionWizardStep(step, !!selectedProfile);
  }, [isOpen, step, selectedProfile]);

  // Charge les profils famille
  useEffect(() => {
    if (!isOpen) return;
    const token = localStorage.getItem('glowp_access_token');
    if (!token) return;
    setLoadingProfiles(true);
    fetch(`${API_BASE_URL}/api/v1/users/profiles`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(data => {
        const list =
          (Array.isArray(data?.data?.profiles) && data.data.profiles) ||
          (Array.isArray(data?.profiles) && data.profiles) ||
          (Array.isArray(data?.data) && data.data) ||
          [];
        setProfiles(list);
      })
      .catch(() => {
        /* swallow */
      })
      .finally(() => setLoadingProfiles(false));
  }, [isOpen]);

  const selectedProfileMeta = useMemo(
    () => profiles.find(p => p.id === selectedProfile) ?? null,
    [profiles, selectedProfile]
  );

  // Catalogue dynamique derive du profile selectionne. On regenere quand
  // le profil change pour que les categories proposees collent au profil
  // (enfant fille n'a pas les memes que homme adulte).
  const missionCategories = useMemo(
    () => getMissionCategories(profileToProductProfile(selectedProfileMeta)),
    [selectedProfileMeta]
  );

  // Quand le catalogue change (changement de profile), on s'assure que
  // les items existants pointent toujours sur une categorie valide.
  // Si la categorie d'un item n'existe pas dans le nouveau profil, on
  // bascule sur la premiere disponible.
  useEffect(() => {
    if (missionCategories.length === 0) return;
    setItems(prev =>
      prev.map(it => {
        const exists = missionCategories.some(c => c.key === it.category);
        if (exists) return it;
        return {
          ...it,
          category: missionCategories[0].key,
          subStyle: undefined,
        };
      })
    );
  }, [missionCategories]);

  // Pre-fill taille + couleurs preferees depuis le profil quand il change.
  // Strategie conservative : on ne remplit qu'un item nouvellement cree
  // (champs vides) — on n'ecrase pas ce que la user a deja saisi.
  useEffect(() => {
    if (!selectedProfileMeta) return;
    const sizeFromProfile = selectedProfileMeta.size_preferences?.clothes;
    const colorsFromProfile = prefsToArray(
      selectedProfileMeta.color_preferences
    )
      .map(c => c.toLowerCase())
      .filter(c => MISSION_COLORS.some(mc => mc.key === c));
    if (!sizeFromProfile && colorsFromProfile.length === 0) return;
    setItems(prev =>
      prev.map(it => {
        const nextSize =
          !it.sizeOverride && sizeFromProfile
            ? sizeFromProfile
            : it.sizeOverride;
        const nextColors =
          it.colors.length === 0 ? colorsFromProfile.slice(0, 2) : it.colors;
        return { ...it, sizeOverride: nextSize, colors: nextColors };
      })
    );
  }, [selectedProfileMeta]);

  const addItem = useCallback(() => {
    const next: MissionItem = {
      id: newItemId(),
      // Premiere categorie du catalogue dynamique en defaut (ex: "hauts"
      // pour adulte femme, "vestes" pour enfant garcon, etc.).
      category: missionCategories[0]?.key ?? 'hauts',
      colors: [],
      quantity: 1,
      sizeOverride: selectedProfileMeta?.size_preferences?.clothes,
    };
    setItems(prev => [...prev, next]);
    setExpandedItemId(next.id);
  }, [selectedProfileMeta, missionCategories]);

  const updateItem = useCallback((id: string, patch: Partial<MissionItem>) => {
    setItems(prev => prev.map(it => (it.id === id ? { ...it, ...patch } : it)));
  }, []);

  const removeItem = useCallback((id: string) => {
    setItems(prev => prev.filter(it => it.id !== id));
  }, []);

  const toggleColor = useCallback((itemId: string, colorKey: string) => {
    setItems(prev =>
      prev.map(it => {
        if (it.id !== itemId) return it;
        const colors = it.colors.includes(colorKey)
          ? it.colors.filter(c => c !== colorKey)
          : [...it.colors, colorKey];
        return { ...it, colors };
      })
    );
  }, []);

  const canProceed = useMemo(() => {
    if (step === 0) return true;
    if (step === 1) return items.length > 0;
    return true;
  }, [step, items]);

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const token = localStorage.getItem('glowp_access_token');
      // categories[] derive de items[] pour compatibilite backend matcher.
      const categoriesUnique = Array.from(
        new Set(items.map(it => it.category))
      );
      const body = {
        items: items.map(it => ({
          category: it.category,
          subStyle: it.subStyle,
          colors: it.colors,
          quantity: it.quantity,
          size: it.sizeOverride,
        })),
        categories: categoriesUnique,
        size: selectedProfileMeta?.size_preferences?.clothes ?? undefined,
        budgetMax: budget ?? undefined,
        profileId: selectedProfile ?? undefined,
        notes: notes.trim() || undefined,
      };
      const res = await fetch(`${API_BASE_URL}/api/v1/shopping-missions`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!data?.success) {
        throw new Error(data?.error || 'Erreur creation mission');
      }
      const missionId = data.data.mission.id;
      submittedRef.current = true;
      analytics.missionCreated({
        itemsCount: items.length,
        distinctCategories: new Set(items.map(it => it.category)).size,
        hasProfile: !!selectedProfile,
        hasBudget: budget != null,
        totalQuantity: items.reduce((s, it) => s + it.quantity, 0),
      });
      onClose();
      navigate(`/missions/${missionId}`);
    } catch (err: any) {
      setError(err?.message || 'Erreur reseau');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const missionTitle = selectedProfileMeta
    ? `Refaire la garde-robe de ${selectedProfileMeta.profile_name}`
    : 'Trouver plusieurs pièces';

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[90] bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center"
        onClick={onClose}
      >
        <motion.div
          initial={{ y: 60, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 60, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          onClick={e => e.stopPropagation()}
          className="bg-brand-offwhite w-full sm:max-w-lg rounded-t-3xl sm:rounded-3xl shadow-2xl max-h-[92dvh] overflow-hidden flex flex-col"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-brand-border">
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-9 h-9 rounded-xl bg-brand-cream flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-5 h-5 text-brand-500" />
              </div>
              <div className="min-w-0">
                <h3 className="text-sm font-bold text-brand-dark truncate">
                  {missionTitle}
                </h3>
                <p className="text-[11px] text-brand-warm">
                  Étape {step + 1} sur 3
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-brand-cream rounded-lg transition-colors flex-shrink-0"
              aria-label="Fermer"
            >
              <X className="w-5 h-5 text-brand-warm" />
            </button>
          </div>

          {/* Progress dots */}
          <div className="flex gap-1.5 px-5 pt-3">
            {[0, 1, 2].map(i => (
              <div
                key={i}
                className={`h-1 rounded-full flex-1 transition-colors ${
                  i <= step ? 'bg-brand-500' : 'bg-brand-border'
                }`}
              />
            ))}
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-5 py-5">
            <AnimatePresence mode="wait">
              {/* Step 0 : Pour qui ? */}
              {step === 0 && (
                <motion.div
                  key="step-0"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                >
                  <h4 className="text-base font-bold text-brand-dark mb-1">
                    Pour qui ?
                  </h4>
                  <p className="text-xs text-brand-warm mb-4">
                    On préremplit tailles et couleurs préférées automatiquement.
                  </p>
                  <button
                    onClick={() => setSelectedProfile(null)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-2xl border-2 mb-2 transition-all ${
                      selectedProfile === null
                        ? 'border-brand-500 bg-brand-cream'
                        : 'border-brand-border bg-white hover:bg-brand-cream'
                    }`}
                  >
                    <div className="w-10 h-10 rounded-full bg-brand-light flex items-center justify-center text-brand-dark font-bold">
                      M
                    </div>
                    <div className="text-left flex-1">
                      <p className="text-sm font-semibold text-brand-dark">
                        Pour moi
                      </p>
                      <p className="text-[11px] text-brand-warm">
                        Aucun profil spécifique
                      </p>
                    </div>
                    {selectedProfile === null && (
                      <Check className="w-5 h-5 text-brand-500" />
                    )}
                  </button>
                  {loadingProfiles && (
                    <p className="text-xs text-brand-warm py-2 text-center">
                      Chargement des profils...
                    </p>
                  )}
                  {profiles.map(p => {
                    const sizeHint = p.size_preferences?.clothes;
                    return (
                      <button
                        key={p.id}
                        onClick={() => setSelectedProfile(p.id)}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-2xl border-2 mb-2 transition-all ${
                          selectedProfile === p.id
                            ? 'border-brand-500 bg-brand-cream'
                            : 'border-brand-border bg-white hover:bg-brand-cream'
                        }`}
                      >
                        <div className="w-10 h-10 rounded-full bg-brand-light flex items-center justify-center overflow-hidden">
                          {p.avatar_url ? (
                            <img
                              src={p.avatar_url}
                              alt=""
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <span className="text-brand-dark font-bold">
                              {p.profile_name.charAt(0).toUpperCase()}
                            </span>
                          )}
                        </div>
                        <div className="text-left flex-1">
                          <p className="text-sm font-semibold text-brand-dark">
                            {p.profile_name}
                          </p>
                          <p className="text-[11px] text-brand-warm">
                            {p.age ? `${p.age} ans` : p.profile_type}
                            {sizeHint && ` · taille ${sizeHint}`}
                          </p>
                        </div>
                        {selectedProfile === p.id && (
                          <Check className="w-5 h-5 text-brand-500" />
                        )}
                      </button>
                    );
                  })}
                  {!loadingProfiles && profiles.length === 0 && (
                    <p className="text-[11px] text-brand-warm text-center py-2">
                      Astuce : ajoute des profils famille pour personnaliser par
                      enfant.
                    </p>
                  )}
                </motion.div>
              )}

              {/* Step 1 : Items */}
              {step === 1 && (
                <motion.div
                  key="step-1"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                >
                  <h4 className="text-base font-bold text-brand-dark mb-1">
                    Que cherches-tu ?
                  </h4>
                  <p className="text-xs text-brand-warm mb-4">
                    Précise un article par carte. Tu peux ajouter plusieurs
                    pièces de la même catégorie (1 jean slim bleu, 1 jean baggy
                    noir…).
                  </p>

                  {items.map(item => (
                    <ItemCard
                      key={item.id}
                      item={item}
                      expanded={expandedItemId === item.id}
                      onToggle={() =>
                        setExpandedItemId(
                          expandedItemId === item.id ? null : item.id
                        )
                      }
                      onUpdate={patch => updateItem(item.id, patch)}
                      onToggleColor={c => toggleColor(item.id, c)}
                      onRemove={() => removeItem(item.id)}
                      canRemove={items.length > 1}
                      categories={missionCategories}
                    />
                  ))}

                  <button
                    onClick={addItem}
                    className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl border-2 border-dashed border-brand-border text-sm font-semibold text-brand-warm hover:bg-brand-cream transition-all"
                  >
                    <Plus className="w-4 h-4" />
                    Ajouter un article
                  </button>
                </motion.div>
              )}

              {/* Step 2 : Budget + notes */}
              {step === 2 && (
                <motion.div
                  key="step-2"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                >
                  <h4 className="text-base font-bold text-brand-dark mb-1">
                    Budget total
                  </h4>
                  <p className="text-xs text-brand-warm mb-4">
                    Approximatif. On prioritise les boutiques dans ton budget
                    total.
                  </p>
                  <div className="grid grid-cols-3 gap-2 mb-5">
                    {BUDGET_PRESETS.map(b => (
                      <button
                        key={b.label}
                        onClick={() => setBudget(b.value)}
                        className={`py-2.5 rounded-xl text-sm font-medium transition-all border-2 ${
                          budget === b.value
                            ? 'bg-brand-500 text-white border-brand-500'
                            : 'bg-white text-brand-dark border-brand-border hover:border-brand-500'
                        }`}
                      >
                        {b.label}
                      </button>
                    ))}
                  </div>

                  <label className="block text-xs font-semibold text-brand-dark mb-1.5">
                    Notes (optionnel)
                  </label>
                  <textarea
                    value={notes}
                    onChange={e => setNotes(e.target.value)}
                    placeholder="Précisions, occasion, contraintes…"
                    rows={3}
                    className="w-full px-3 py-2.5 rounded-xl border-2 border-brand-border focus:border-brand-500 focus:outline-none text-sm bg-white resize-none"
                  />

                  {/* Recap */}
                  <div className="mt-5 rounded-2xl bg-brand-cream p-4">
                    <p className="text-[11px] uppercase tracking-wider font-semibold text-brand-warm mb-2">
                      Récap mission
                    </p>
                    <ul className="text-xs text-brand-dark space-y-1">
                      {items.map(it => {
                        const cat = missionCategories.find(
                          c => c.key === it.category
                        );
                        const colorLabels = it.colors
                          .map(
                            ck =>
                              MISSION_COLORS.find(mc => mc.key === ck)?.label
                          )
                          .filter(Boolean)
                          .join(', ');
                        const subStyleLabel = it.subStyle
                          ? (cat?.subStyles.find(s => s.key === it.subStyle)
                              ?.label ?? it.subStyle)
                          : '';
                        return (
                          <li key={it.id} className="flex items-start gap-2">
                            <span className="font-semibold">
                              {it.quantity}×
                            </span>
                            <span>
                              {subStyleLabel || cat?.label || it.category}
                              {colorLabels && ` (${colorLabels})`}
                              {it.sizeOverride && ` · ${it.sizeOverride}`}
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {error && (
              <p className="mt-4 text-xs text-red-700 bg-red-50 px-3 py-2 rounded-lg">
                {error}
              </p>
            )}
          </div>

          {/* Footer */}
          <div
            className="border-t border-brand-border px-5 py-4 bg-white flex items-center gap-3"
            style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
          >
            {step > 0 && (
              <button
                onClick={() => setStep((step - 1) as 0 | 1 | 2)}
                className="text-sm font-medium text-brand-warm hover:text-brand-dark px-4 py-2.5"
              >
                Retour
              </button>
            )}
            <button
              onClick={() => {
                if (step < 2) setStep((step + 1) as 0 | 1 | 2);
                else handleSubmit();
              }}
              disabled={!canProceed || submitting}
              className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold transition-all ${
                canProceed && !submitting
                  ? 'bg-brand-dark text-brand-cream hover:bg-brand-deep'
                  : 'bg-brand-border text-brand-muted cursor-not-allowed'
              }`}
            >
              {submitting
                ? 'Recherche en cours...'
                : step < 2
                  ? 'Suivant'
                  : 'Trouver les boutiques'}
              {!submitting && step < 2 && <ArrowRight className="w-4 h-4" />}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// ============================================================================
// ItemCard : un article dans la mission. Accordeon : header compact +
// editeur deplie. Quand non-deplie, on resume la config en une ligne.
// ============================================================================

const ItemCard: React.FC<{
  item: MissionItem;
  expanded: boolean;
  onToggle: () => void;
  onUpdate: (patch: Partial<MissionItem>) => void;
  onToggleColor: (colorKey: string) => void;
  onRemove: () => void;
  canRemove: boolean;
  categories: ReturnType<typeof getMissionCategories>;
}> = ({
  item,
  expanded,
  onToggle,
  onUpdate,
  onToggleColor,
  onRemove,
  canRemove,
  categories,
}) => {
  const cat = categories.find(c => c.key === item.category);
  const subStyleLabel = item.subStyle
    ? (cat?.subStyles.find(s => s.key === item.subStyle)?.label ??
      item.subStyle)
    : '';
  const colorLabels = item.colors
    .map(ck => MISSION_COLORS.find(mc => mc.key === ck)?.label)
    .filter(Boolean)
    .join(', ');

  return (
    <div className="rounded-2xl border-2 border-brand-border bg-white mb-3 overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-brand-cream transition-colors"
      >
        <div className="w-10 h-10 rounded-xl bg-brand-cream flex items-center justify-center text-base font-black text-brand-dark">
          {item.quantity}×
        </div>
        <div className="text-left flex-1 min-w-0">
          <p className="text-sm font-bold text-brand-dark truncate">
            {subStyleLabel || cat?.label || item.category}
            {subStyleLabel && cat && (
              <span className="font-normal text-brand-warm">
                {' '}
                · {cat.label}
              </span>
            )}
          </p>
          <p className="text-[11px] text-brand-warm truncate">
            {colorLabels || 'Toutes couleurs'}
            {item.sizeOverride && ` · taille ${item.sizeOverride}`}
          </p>
        </div>
        {canRemove && (
          <button
            onClick={e => {
              e.stopPropagation();
              onRemove();
            }}
            className="p-2 rounded-lg hover:bg-red-50 text-red-500 transition-colors"
            aria-label="Supprimer cet article"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </button>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden border-t border-brand-border"
          >
            <div className="p-4 space-y-4">
              {/* Categorie */}
              <div>
                <label className="block text-xs font-semibold text-brand-dark mb-1.5">
                  Catégorie
                </label>
                <select
                  value={item.category}
                  onChange={e =>
                    onUpdate({ category: e.target.value, subStyle: undefined })
                  }
                  className="w-full px-3 py-2.5 rounded-xl border-2 border-brand-border bg-white text-sm focus:border-brand-500 focus:outline-none"
                >
                  {categories.map(c => (
                    <option key={c.key} value={c.key}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Sub-style chips */}
              {cat?.subStyles && cat.subStyles.length > 0 && (
                <div>
                  <label className="block text-xs font-semibold text-brand-dark mb-1.5">
                    Style (optionnel)
                  </label>
                  <div className="flex flex-wrap gap-1.5">
                    <button
                      onClick={() => onUpdate({ subStyle: undefined })}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                        !item.subStyle
                          ? 'bg-brand-dark text-brand-cream border-brand-dark'
                          : 'bg-white text-brand-dark border-brand-border hover:border-brand-500'
                      }`}
                    >
                      Indifférent
                    </button>
                    {cat.subStyles.map(s => (
                      <button
                        key={s.key}
                        onClick={() => onUpdate({ subStyle: s.key })}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                          item.subStyle === s.key
                            ? 'bg-brand-500 text-white border-brand-500'
                            : 'bg-white text-brand-dark border-brand-border hover:border-brand-500'
                        }`}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Couleurs */}
              <div>
                <label className="block text-xs font-semibold text-brand-dark mb-1.5">
                  Couleurs souhaitées
                </label>
                <div className="flex flex-wrap gap-1.5">
                  {MISSION_COLORS.map(col => {
                    const active = item.colors.includes(col.key);
                    return (
                      <button
                        key={col.key}
                        onClick={() => onToggleColor(col.key)}
                        className={`flex items-center gap-1.5 pl-1.5 pr-2.5 py-1 rounded-full text-xs font-medium border transition-all ${
                          active
                            ? 'bg-brand-dark text-brand-cream border-brand-dark'
                            : 'bg-white text-brand-dark border-brand-border'
                        }`}
                      >
                        <span
                          className="w-4 h-4 rounded-full border border-black/10"
                          style={{ background: col.swatch }}
                        />
                        {col.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Quantite + taille override */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-brand-dark mb-1.5">
                    Quantité
                  </label>
                  <div className="flex items-center gap-2 border-2 border-brand-border rounded-xl px-2 py-1 bg-white">
                    <button
                      onClick={() =>
                        onUpdate({
                          quantity: Math.max(1, item.quantity - 1),
                        })
                      }
                      className="w-8 h-8 rounded-lg hover:bg-brand-cream text-brand-dark font-bold"
                      aria-label="Diminuer"
                    >
                      −
                    </button>
                    <input
                      type="number"
                      min={1}
                      max={20}
                      value={item.quantity}
                      onChange={e =>
                        onUpdate({
                          quantity: Math.max(
                            1,
                            Math.min(20, parseInt(e.target.value) || 1)
                          ),
                        })
                      }
                      className="w-full text-center bg-transparent focus:outline-none text-sm font-bold text-brand-dark"
                    />
                    <button
                      onClick={() =>
                        onUpdate({
                          quantity: Math.min(20, item.quantity + 1),
                        })
                      }
                      className="w-8 h-8 rounded-lg hover:bg-brand-cream text-brand-dark font-bold"
                      aria-label="Augmenter"
                    >
                      +
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-brand-dark mb-1.5">
                    Taille (optionnel)
                  </label>
                  <input
                    type="text"
                    value={item.sizeOverride ?? ''}
                    onChange={e =>
                      onUpdate({ sizeOverride: e.target.value || undefined })
                    }
                    placeholder="3 ans, 38, M…"
                    className="w-full px-3 py-2.5 rounded-xl border-2 border-brand-border bg-white text-sm focus:border-brand-500 focus:outline-none"
                  />
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ShoppingMissionWizard;
